// subscriptionGates.js — Centralized Subscription & Feature Access Control
// UPDATED: Aligned to 2026 pricing strategy research
// Tiers: Freemium ($0) | Creator ($14.99) | Growth ($49.99) | Business ($149.99)
// Old tier names (starter/scale/executive) removed from public-facing config.
// Executive retained internally for legacy/custom contracts only.

export const SUBSCRIPTION_TIERS = {
  FREEMIUM: 'freemium',
  CREATOR: 'creator',      // $14.99/mo — solo creators, TikTok/Reels focused
  GROWTH: 'growth',        // $49.99/mo — power users, solopreneurs, micro-agencies
  BUSINESS: 'business',    // $149.99/mo — SMB teams and agencies
  EXECUTIVE: 'executive'   // Custom — legacy/enterprise contracts only
};

// ── Stripe Live Price IDs ────────────────────────────────────────────────────
// These map directly to the products created in the Stripe Live dashboard.
// DO NOT use these in frontend code — only reference via the backend.
export const STRIPE_PRICE_IDS = {
  [SUBSCRIPTION_TIERS.CREATOR]:  'price_1TddlLDfvr62XlVxIgtsc2Et',
  [SUBSCRIPTION_TIERS.GROWTH]:   'price_1Tddm2Dfvr62XlVxpkoBBDm8',
  [SUBSCRIPTION_TIERS.BUSINESS]: 'price_1TddmfDfvr62XlVxPyJi7WXu',
};

// ── Credit costs per AI operation ────────────────────────────────────────────
// Based on 2026 pricing report: markup 2x–3x over raw API cost.
// These match the backend CREDIT_COSTS in subscriptionMiddleware.js.
export const CREDIT_COSTS = {
  CAPTION_GENERATION:   1,   // ~$0.001 raw cost
  HASHTAG_GENERATION:   1,
  CONTENT_IDEA:         2,
  IMAGE_GENERATION:     15,  // ~$0.05 raw (Gemini Imagen)
  IMAGE_EDIT:           15,
  VIDEO_ENHANCEMENT:    50,  // Cloudinary smart crop + optimize
  VIDEO_CAPTION:        50,  // AssemblyAI transcription per video
  VIDEO_GENERATIVE:     500, // Full Mux → Gemini → Shotstack triad
  BACKGROUND_REMOVAL:   100, // Cloudinary background_removal add-on
  BRAND_ANALYSIS:       5,
  COMPETITOR_ANALYSIS:  10,
};

// ── Feature flags ─────────────────────────────────────────────────────────────
export const FEATURES = {
  // Core
  CONTENT_LAB:        'content_lab',
  MEDIA_LIBRARY:      'media_library',
  FEED_PREVIEW:       'feed_preview',
  BRAND_WIZARD:       'brand_wizard',

  // AI generation
  AI_CAPTIONS:        'ai_captions',
  AI_HASHTAGS:        'ai_hashtags',
  AI_CONTENT_IDEAS:   'ai_content_ideas',
  AI_IMAGE_GENERATION:'ai_image_generation',
  AI_VIDEO_TOOLS:     'ai_video_tools',
  AI_VIDEO_TRIAD:     'ai_video_triad',     // Mux → Gemini → Shotstack pipeline

  // Analytics
  BASIC_ANALYTICS:    'basic_analytics',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  PREDICTIVE_ANALYTICS:'predictive_analytics',

  // Publishing
  BASIC_SCHEDULING:   'basic_scheduling',
  ADVANCED_SCHEDULING:'advanced_scheduling',
  AUTO_POSTING:       'auto_posting',

  // Team / Enterprise
  TEAM_COLLABORATION: 'team_collaboration',
  API_ACCESS:         'api_access',
  WHITE_LABEL:        'white_label',
  DEDICATED_SUPPORT:  'dedicated_support',

  // Compounding Loop (Growth+ only)
  COMPOUNDING_LOOP:   'compounding_loop',   // Cross-platform performance analytics + brand voice training
  BRAND_VOICE_TRAINING:'brand_voice_training',
};

// ── Quotas per tier ───────────────────────────────────────────────────────────
// Credit allocations derived from pricing report:
//   Creator  = ~15–20 short-form videos/mo
//   Growth   = 4x Creator allotment + soft cap with overage
//   Business = Workflow-credit model, high allotment
export const TIER_QUOTAS = {
  [SUBSCRIPTION_TIERS.FREEMIUM]: {
    monthlyGenerativeCredits: 100,     // ~100 captions OR ~6 images (hard cap, no overage)
    mediaStorageGB:           0.5,
    maxUserSeats:             1,
    maxSocialAccounts:        1,
    hasOverageAllowed:        false,
    hasSoftCap:               false,
  },

  [SUBSCRIPTION_TIERS.CREATOR]: {
    monthlyGenerativeCredits: 1500,    // ~1500 captions OR ~100 images OR ~15 short videos
    mediaStorageGB:           10,
    maxUserSeats:             1,
    maxSocialAccounts:        3,
    hasOverageAllowed:        true,
    hasSoftCap:               true,
    overageCostPerCredit:     0.02,    // $0.02/credit after soft cap
    hasBasicAnalytics:        true,
  },

  [SUBSCRIPTION_TIERS.GROWTH]: {
    monthlyGenerativeCredits: 6000,    // 4x Creator; ~6000 captions OR ~400 images OR ~60 short videos
    mediaStorageGB:           50,
    maxUserSeats:             5,
    maxSocialAccounts:        10,
    hasOverageAllowed:        true,
    hasSoftCap:               true,
    overageCostPerCredit:     0.015,   // Lower overage rate
    hasBasicAnalytics:        true,
    hasAdvancedAnalytics:     true,
    hasAutoPosting:           true,
    hasCompoundingLoop:       true,    // Unlocks brand voice training + cross-platform analytics
  },

  [SUBSCRIPTION_TIERS.BUSINESS]: {
    monthlyGenerativeCredits: 20000,   // Workflow-credit model; team-scale volume
    mediaStorageGB:           200,
    maxUserSeats:             10,
    maxSocialAccounts:        30,
    hasOverageAllowed:        true,
    hasSoftCap:               true,
    overageCostPerCredit:     0.01,    // Best overage rate
    hasBasicAnalytics:        true,
    hasAdvancedAnalytics:     true,
    hasPredictiveAnalytics:   true,
    hasAutoPosting:           true,
    hasTeamCollaboration:     true,
    hasApiAccess:             true,
    hasCompoundingLoop:       true,
  },

  [SUBSCRIPTION_TIERS.EXECUTIVE]: {
    monthlyGenerativeCredits: 100000,
    mediaStorageGB:           1000,
    maxUserSeats:             100,
    maxSocialAccounts:        999,
    hasOverageAllowed:        true,
    hasSoftCap:               false,
    overageCostPerCredit:     0.0,
    hasBasicAnalytics:        true,
    hasAdvancedAnalytics:     true,
    hasPredictiveAnalytics:   true,
    hasAutoPosting:           true,
    hasTeamCollaboration:     true,
    hasApiAccess:             true,
    hasWhiteLabel:            true,
    hasDedicatedSupport:      true,
    hasCompoundingLoop:       true,
  },
};

// ── Public-facing pricing ─────────────────────────────────────────────────────
// Source of truth for PricingPage.jsx and any marketing copy.
export const TIER_PRICING = {
  [SUBSCRIPTION_TIERS.FREEMIUM]: {
    monthlyPrice:  0,
    annualPrice:   0,
    displayPrice:  '$0',
    description:   'Try the platform and see what the loop feels like.',
    ctaLabel:      'Start free',
    popular:       false,
  },

  [SUBSCRIPTION_TIERS.CREATOR]: {
    monthlyPrice:  14.99,
    annualPrice:   149.90,  // 2 months free (~17% off)
    displayPrice:  '$14.99',
    description:   'Essential tools for TikTok and Reels creators posting consistently.',
    ctaLabel:      'Start creating',
    popular:       false,
    stripeMonthlyPriceId: 'price_1TddlLDfvr62XlVxIgtsc2Et',
  },

  [SUBSCRIPTION_TIERS.GROWTH]: {
    monthlyPrice:  49.99,
    annualPrice:   499.90,  // 2 months free
    displayPrice:  '$49.99',
    description:   'Full compounding loop — analytics, brand voice, and cross-platform publishing.',
    ctaLabel:      'Unlock the loop',
    popular:       true,
    stripeMonthlyPriceId: 'price_1Tddm2Dfvr62XlVxpkoBBDm8',
  },

  [SUBSCRIPTION_TIERS.BUSINESS]: {
    monthlyPrice:  149.99,
    annualPrice:   1499.90, // 2 months free
    displayPrice:  '$149.99',
    description:   'Team seats, workflow automation, and API access for agencies and SMB teams.',
    ctaLabel:      'Scale your team',
    popular:       false,
    stripeMonthlyPriceId: 'price_1TddmfDfvr62XlVxPyJi7WXu',
  },

  [SUBSCRIPTION_TIERS.EXECUTIVE]: {
    monthlyPrice:  null,
    annualPrice:   null,
    displayPrice:  'Custom',
    description:   'White-label, dedicated support, and custom contracts.',
    ctaLabel:      'Contact us',
    popular:       false,
    minimumACV:    30000,
  },
};

// ── Feature access helpers ────────────────────────────────────────────────────

const FREEMIUM_FEATURES = [
  FEATURES.CONTENT_LAB,
  FEATURES.MEDIA_LIBRARY,
  FEATURES.FEED_PREVIEW,
  FEATURES.BRAND_WIZARD,
  FEATURES.AI_CAPTIONS,
  FEATURES.AI_HASHTAGS,
  FEATURES.AI_CONTENT_IDEAS,
  FEATURES.BASIC_SCHEDULING,
];

const CREATOR_FEATURES = [
  ...FREEMIUM_FEATURES,
  FEATURES.AI_IMAGE_GENERATION,
  FEATURES.AI_VIDEO_TOOLS,
  FEATURES.BASIC_ANALYTICS,
  FEATURES.AUTO_POSTING,
];

const GROWTH_FEATURES = [
  ...CREATOR_FEATURES,
  FEATURES.ADVANCED_ANALYTICS,
  FEATURES.ADVANCED_SCHEDULING,
  FEATURES.TEAM_COLLABORATION,
  FEATURES.COMPOUNDING_LOOP,
  FEATURES.BRAND_VOICE_TRAINING,
  FEATURES.AI_VIDEO_TRIAD,
];

const BUSINESS_FEATURES = [
  ...GROWTH_FEATURES,
  FEATURES.PREDICTIVE_ANALYTICS,
  FEATURES.API_ACCESS,
];

export function hasFeatureAccess(userTier, feature) {
  const tier = userTier || SUBSCRIPTION_TIERS.FREEMIUM;

  if (tier === SUBSCRIPTION_TIERS.EXECUTIVE) return true;
  if (tier === SUBSCRIPTION_TIERS.BUSINESS)  return BUSINESS_FEATURES.includes(feature);
  if (tier === SUBSCRIPTION_TIERS.GROWTH)    return GROWTH_FEATURES.includes(feature);
  if (tier === SUBSCRIPTION_TIERS.CREATOR)   return CREATOR_FEATURES.includes(feature);
  return FREEMIUM_FEATURES.includes(feature);
}

export function getQuotas(userTier) {
  const tier = userTier || SUBSCRIPTION_TIERS.FREEMIUM;
  return TIER_QUOTAS[tier] || TIER_QUOTAS[SUBSCRIPTION_TIERS.FREEMIUM];
}

export function getPricing(tier) {
  return TIER_PRICING[tier] || TIER_PRICING[SUBSCRIPTION_TIERS.FREEMIUM];
}

export function calculateCreditCost(operation, quantity = 1) {
  const costPer = CREDIT_COSTS[operation] || 0;
  return costPer * quantity;
}

export function hasCredits(userUsage, tier, requiredCredits) {
  const quotas = getQuotas(tier);
  const used    = Number(userUsage?.monthlyGenerativeCreditsUsed || 0);
  const allowed = Number(quotas?.monthlyGenerativeCredits || 0);
  return used + requiredCredits <= allowed;
}

export function getUpgradeMessage(feature) {
  const map = {
    [FEATURES.AI_IMAGE_GENERATION]:   'Upgrade to Creator to unlock AI image generation.',
    [FEATURES.AI_VIDEO_TOOLS]:        'Upgrade to Creator to unlock AI video tools.',
    [FEATURES.BASIC_ANALYTICS]:       'Upgrade to Creator to unlock analytics.',
    [FEATURES.ADVANCED_ANALYTICS]:    'Upgrade to Growth to unlock advanced analytics.',
    [FEATURES.COMPOUNDING_LOOP]:      'Upgrade to Growth to unlock the compounding intelligence loop.',
    [FEATURES.BRAND_VOICE_TRAINING]:  'Upgrade to Growth to unlock brand voice training.',
    [FEATURES.AI_VIDEO_TRIAD]:        'Upgrade to Growth to unlock the AI video pipeline.',
    [FEATURES.PREDICTIVE_ANALYTICS]:  'Upgrade to Business to unlock predictive analytics.',
    [FEATURES.API_ACCESS]:            'Upgrade to Business to unlock API access.',
    [FEATURES.WHITE_LABEL]:           'Contact us for Executive (custom) pricing.',
  };
  return map[feature] || 'Upgrade your plan to unlock this feature.';
}

export function getOverageCost(tier, creditsOver) {
  const quotas = getQuotas(tier);
  if (!quotas?.hasOverageAllowed) return null;
  const rate = Number(quotas.overageCostPerCredit || 0);
  return rate * Number(creditsOver || 0);
}

// ── Backward-compat aliases ───────────────────────────────────────────────────
// The old tier names (starter, scale) may still exist in some user records in MongoDB.
// These aliases allow the middleware and gate checks to resolve them gracefully
// without a database migration.
export const TIER_ALIASES = {
  starter:    SUBSCRIPTION_TIERS.CREATOR,
  scale:      SUBSCRIPTION_TIERS.BUSINESS,
  pro:        SUBSCRIPTION_TIERS.CREATOR,
  enterprise: SUBSCRIPTION_TIERS.EXECUTIVE,
};

export function resolveTier(rawTier) {
  if (!rawTier) return SUBSCRIPTION_TIERS.FREEMIUM;
  return TIER_ALIASES[rawTier] || rawTier;
}